/**
 * Smooths an array of elevation values using a simple moving average.
 * 
 * @param elevations - The array of elevation numbers.
 * @param window - The size of the moving average window (number of points). Should be an odd number for symmetric averaging. Defaults to 5.
 * @returns A new array containing the smoothed elevation values.
 */
export function smoothElevations(elevations: number[], window: number = 5): number[] {
  if (window <= 1 || elevations.length <= window) {
    // No smoothing needed or possible if window is too small or data is too short
    return [...elevations];
  }

  // Ensure window size is odd for a symmetric window around the center point
  const windowSize = Math.floor(window / 2);

  return elevations.map((_, index, arr) => {
    // Define the slice boundaries, clamping to the array edges
    const start = Math.max(0, index - windowSize);
    const end = Math.min(arr.length, index + windowSize + 1); // +1 because slice end is exclusive
    
    // Get the slice of elevations within the window
    const slice = arr.slice(start, end);
    
    // Calculate the average of the slice
    const sum = slice.reduce((acc, val) => acc + val, 0);
    return sum / slice.length;
  });
} 